import { Injectable, inject } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

import { AlertService } from './alert.service';

export interface LocationColor {
  label: string;
  color: string;
  textColor: string;
}

const normalizeLabel = (value: unknown): string => `${value ?? ''}`.trim().toLowerCase();

/**
 * Keeps the queue's location colours in step with Manage Camera Location Options, the
 * same way `PriorityThemeService` keeps the priority colours in step with the Priority
 * section right beside it.
 *
 * An alert still snapshots `alert_location_color` / `alert_location_text_color` at the
 * moment it fires (alert.controller.js) — that stays, it is what a camera without a
 * matching option today falls back to. But a location an operator RENAMES or RECOLOURS
 * later should retint every alert that carries it, past and future, the same way
 * changing a priority's colour retints every alert at that level; a per-alert snapshot
 * cannot do that on its own; only a live lookup by the alert's location NAME can.
 */
@Injectable({ providedIn: 'root' })
export class LocationThemeService {
  private alertService = inject(AlertService);

  /** Last known list, for the settings screen to render without re-fetching. */
  readonly colors$ = new BehaviorSubject<LocationColor[]>([]);

  private byLabel = new Map<string, LocationColor>();

  /**
   * Called once at app start, alongside PriorityThemeService. Failure is silent: alerts
   * simply keep reading their own snapshotted colour instead of the live one.
   */
  load(): void {
    this.alertService.getAlertLocationDropdownOptions().subscribe({
      next: (res: any) => {
        const rows: LocationColor[] = (res?.data || []).map((row: any) => ({
          label: `${row?.labelName ?? ''}`.trim(),
          color: `${row?.color ?? ''}`.trim(),
          textColor: `${row?.text_color ?? ''}`.trim(),
        }));
        this.apply(rows);
      },
      error: () => {
        // Keep whatever alerts already carry.
      },
    });
  }

  /** Rebuilds the lookup immediately, so the settings screen previews live too. */
  apply(rows: LocationColor[]): void {
    this.byLabel = new Map(rows.filter((row) => row.label).map((row) => [normalizeLabel(row.label), row]));
    this.colors$.next(rows);
  }

  /**
   * The CURRENT colour for a location name, or null if nothing configured today still
   * carries that name (it was renamed or deleted since the alert fired).
   */
  colorFor(label: unknown): string | null {
    return this.byLabel.get(normalizeLabel(label))?.color || null;
  }

  textColorFor(label: unknown): string | null {
    return this.byLabel.get(normalizeLabel(label))?.textColor || null;
  }
}
